import { useCallback } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import type { RootState } from "@/store/store";
import type { RouteConfig } from "@/routes/routes.types";
import { getModuleEntry } from "@/store/slices/metricsCatalog/catalogSelectors";
import { resolveLabel } from "@/utils/catalogLabels";
import { ROUTES_CHILDREN_LOOKUP } from "@/routes/routes";
import { MARKETS, POINT_IN_TIME } from "@/constant/constantsThrougoutProject";

/**
 * Route `key` spellings that differ from the metrics-catalog `moduleKey`
 * beyond what `getModuleEntry` already normalizes (it handles
 * `emotional` → `imagery` and `aivisibility` → `ai_visibility`).
 *
 * Anything absent here is passed through as-is.
 */
const ROUTE_KEY_TO_MODULE: Record<string, string> = {
  reputationQrq: "reputation",
};

/** A route reduced to what labelling needs. */
type LabellableRoute = Pick<RouteConfig, "key" | "name">;

/**
 * Overloaded so a known route yields a plain `string` — every route carries a
 * `name`, so a label always resolves — while a possibly-absent one keeps the
 * `undefined` in its type rather than hiding it from the caller.
 */
export interface RouteLabelResolver {
  (route: LabellableRoute): string;
  (route: LabellableRoute | null | undefined): string | undefined;
}

/**
 * Resolves nav/page titles from the metric catalog's module labels.
 *
 * Replaces the per-market `app/attribute-labels` call: the catalog is already
 * fetched at bootstrap for every accessible market (see `userDetail.ts`), and
 * each module carries `label` plus sparse `labelOverrides` keyed by market id —
 * the same canonical-plus-override shape, resolved by `resolveLabel`.
 *
 * Market resolution mirrors the behaviour the attribute-labels selectors had:
 *
 * - On a route that allows multiple markets at point-in-time, no single market
 *   owns the title, so the canonical `label` is used (this is what the old
 *   `"global"` key held).
 * - Otherwise the first selected market's override wins, falling back to the
 *   canonical `label` (the old `"default"` key).
 *
 * Routes with no catalog module — `exclusivequestions` and `reports` — fall
 * back to the route's own `name`, as they did whenever attribute-labels
 * carried no entry for them.
 */
export function useRouteLabel(): RouteLabelResolver {
  const { pathname } = useLocation();

  // The catalog object is replaced wholesale at bootstrap and never mutated,
  // so reference equality is correct here — a deep compare would walk every
  // module and metric on each dispatch for no benefit.
  const catalog = useSelector(
    (state: RootState) => state.activeGroup.metricsCatalog.catalog,
  );

  // Selected as a primitive rather than the markets array, so the default
  // reference equality check is enough.
  const selectedMarketId = useSelector((state: RootState) =>
    state.userSelection.markets?.length > 0
      ? state.userSelection.markets[0]
      : undefined,
  );

  const isMultiMarketRoute = Boolean(
    ROUTES_CHILDREN_LOOKUP[pathname]?.multipleOptionsForKeys?.[
      POINT_IN_TIME
    ]?.includes(MARKETS),
  );

  const marketId = isMultiMarketRoute ? undefined : selectedMarketId;

  // The single implementation satisfies both overloads; `useCallback` can only
  // infer one signature, hence the assertion.
  return useCallback(
    (route: LabellableRoute | null | undefined) => {
      if (!route) return undefined;
      const moduleKey = ROUTE_KEY_TO_MODULE[route.key] ?? route.key;
      const moduleEntry = getModuleEntry(catalog, moduleKey);
      return moduleEntry ? resolveLabel(moduleEntry, marketId) : route.name;
    },
    [catalog, marketId],
  ) as RouteLabelResolver;
}
